import { useMemo, useRef, useState } from 'react';
import { cn, useOutsideClick } from 'common/utils';
import { CaretIcon } from 'components/v2/atoms/icon';
import TopicSelectionDialog from 'components/v2/molecules/topic-selection-dialog';
import { useMutation, useQuery } from 'react-query';
import { createPostTopic } from 'service/post';
import { getUsersTopics, UserTopicType } from 'service/user';
import useGlobalStore from 'store/global';

const ChooseTopic = ({
  selectedTopicId,
  setSelectedTopicId,
}: {
  selectedTopicId: number;
  setSelectedTopicId: (topicId: number) => void;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  useOutsideClick(containerRef, () => {
    if (isModalOpen) setIsModalOpen(false);
  });

  const handleTopicSelect = (topic: UserTopicType) => {
    setSelectedTopicId(topic.id);
    setIsModalOpen(false);
  };

  const { data, refetch: refetchUsersTopics } = useQuery(
    'getUsersTopics',
    async () => {
      return await getUsersTopics();
    },
    {
      refetchOnWindowFocus: false,
      enabled: isAuthInit,
      retry: false,
      staleTime: 15 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
    },
  );

  const { mutate: handleCreatePostTopic } = useMutation(
    async (topicName: string) => {
      return await createPostTopic(topicName);
    },
    {
      onSuccess: (data) => {
        if (data.data.length > 0 && data.data?.[0]?.id) {
          void refetchUsersTopics();
          handleTopicSelect(data.data[0]);
          setSnackbarMessage({
            type: 'success',
            text: 'トピックを作成しました',
          });
        }
      },
      onError: () => {
        setSnackbarMessage({
          type: 'error',
          text: 'エラーが発生しました',
        });
      },
    },
  );

  const handleCreateNewTopic = (topicName: string) => {
    const topicNameWithoutSpace = topicName.trim();
    if (
      !topicNameWithoutSpace ||
      topicNameWithoutSpace.length === 0 ||
      topicNameWithoutSpace.length > 12
    ) {
      setSnackbarMessage({
        type: 'error',
        text: 'トピック名は12文字以内で入力してください',
      });
      return;
    }
    handleCreatePostTopic(topicNameWithoutSpace);
  };

  const topics = useMemo(() => {
    return [...(data?.data || [])];
  }, [data?.data]);
  const currentTopicName = useMemo(() => {
    return topics.find((topic) => topic.id === selectedTopicId)?.name || '';
  }, [topics, selectedTopicId]);
  return (
    <div ref={containerRef}>
      <div
        className="flex cursor-pointer items-center space-x-2 rounded-lg bg-pp-bg-tertiary px-4 py-3 hover:bg-pp-border-primary"
        onClick={() => setIsModalOpen(!isModalOpen)}
      >
        <span className="text-[15px] font-normal text-pp-text-primary">
          トピック:
        </span>
        <div className="relative flex-1">
          <button
            className={cn(
              'w-full text-left text-[15px] font-medium text-pp-text-primary focus:outline-none',
            )}
          >
            {currentTopicName || '選択してください'}
          </button>
        </div>
        <CaretIcon
          className={cn(
            'ml-auto size-4 text-pp-text-tertiary transition-transform',
            {
              'rotate-180': isModalOpen,
            },
          )}
        />
      </div>
      <TopicSelectionDialog
        isOpen={isModalOpen}
        onSelect={handleTopicSelect}
        topics={topics}
        onCreateNew={handleCreateNewTopic}
        triggerRef={containerRef}
      />
    </div>
  );
};

export default ChooseTopic;
